import { Injectable, Injector } from '@angular/core';
import {
  HttpEvent, HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { LoginService } from './_services/domainservice/login.service';

/**
 * AuthInterceptor — NEW (Login-Test-Report.md Issue #2 fix).
 *
 * Login.ts stores the real access token under the `accessToken` localStorage key
 * (`localStorage.setItem('accessToken', res.accessToken)`) but nothing in this file set originally
 * ever attached it to outgoing requests — every domain/admin service just calls
 * `this.http.get/post(...)` with no Authorization header, so any backend action that requires auth
 * would 401 in practice. `attachToken` below is that original, minimal fix: read `accessToken` from
 * localStorage and attach it as a `Bearer` header to every outgoing request that doesn't already
 * carry an Authorization header.
 *
 * FIXED (401-after-token-expiry report): the access token is short-lived by design
 * (JwtOptions.AccessTokenLifetimeMinutes, 15 min default) — this class's own header comment used to
 * flag "silent refresh-token retry on 401" as deliberately out of scope. That gap is exactly what
 * broke the subscription-renewal flow: a user can easily spend more than 15 minutes reading the
 * grace-period notice, navigating to Admin/SubscriptionAdmin, and typing in ClientRegistrationUId/
 * ProductUId by hand, so the token from login was already expired by the time they clicked "Load" —
 * AuthController.cs's JWT bearer validation rejects an expired token with 401 before the request
 * ever reaches SubscriptionController's own role check, which is why it showed as
 * "401 Unauthorized" rather than a role-based 403.
 *
 * intercept() now catches a 401 on any request that ISN'T the login/refresh/oauth endpoints
 * themselves (those are [AllowAnonymous] and retrying them the same way would either be pointless
 * or recurse), calls AuthController's already-existing POST api/auth/refresh with the stored
 * refreshToken, and retries the original request once with the fresh access token. If the refresh
 * itself fails (refresh token missing, expired, or revoked), the session is cleared and the user is
 * sent back to /login — the same "session expired, sign in again" outcome a hard refresh failure
 * always implied, just reached automatically instead of via a confusing 401 on an unrelated screen.
 *
 * Concurrent requests failing at the same moment (e.g. SubscriptionAdmin's `load()` firing both
 * getCurrent and getPlans together) share ONE refresh call via `isRefreshing`/`refreshedToken$`
 * rather than each independently hitting api/auth/refresh — a second refresh call would invalidate
 * the first one's already-in-flight token in most refresh-token implementations otherwise.
 *
 * LoginService is resolved lazily via Injector.get(...) inside handle401(), not constructor-injected,
 * to avoid the classic Angular HTTP_INTERCEPTORS circular-DI trap: LoginService itself depends on
 * HttpClient, and HttpClient's own provider graph depends on HTTP_INTERCEPTORS — constructor-injecting
 * a service that transitively needs HttpClient into a class Angular constructs while building the
 * HTTP_INTERCEPTORS token can trip "NG0200: Circular dependency in DI detected"; resolving it lazily
 * at call time sidesteps that entirely.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private readonly refreshedToken$ = new BehaviorSubject<string | null>(null);

  constructor(
    private readonly router: Router,
    private readonly injector: Injector,
  ) { }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(this.attachToken(req)).pipe(
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse && error.status === 401 && !this.isAuthEndpoint(req.url)) {
          return this.handle401(req, next);
        }
        return throwError(() => error);
      }),
    );
  }

  private attachToken(req: HttpRequest<unknown>): HttpRequest<unknown> {
    const token = localStorage.getItem('accessToken');
    if (!token || req.headers.has('Authorization')) {
      return req;
    }
    return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  // login/refresh/oauth are [AllowAnonymous] server-side and never need (or should trigger) a
  // refresh-and-retry cycle — retrying "refresh" itself on its own 401 would recurse forever.
  private isAuthEndpoint(url: string): boolean {
    return /\/api\/auth\/(login|refresh|oauth\/google|oauth\/microsoft)(\?|$)/i.test(url);
  }

  private handle401(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      this.forceLogout();
      return throwError(() => new HttpErrorResponse({ status: 401, statusText: 'Session expired' }));
    }

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshedToken$.next(null);

      const loginService = this.injector.get(LoginService);

      return loginService.refresh(refreshToken).pipe(
        switchMap((res: any) => {
          this.isRefreshing = false;
          if (!res?.accessToken) {
            this.forceLogout();
            return throwError(() => new HttpErrorResponse({ status: 401, statusText: 'Session expired' }));
          }
          localStorage.setItem('accessToken', res.accessToken);
          localStorage.setItem('token', res.accessToken);
          if (res.refreshToken) { localStorage.setItem('refreshToken', res.refreshToken); }
          this.refreshedToken$.next(res.accessToken);
          return next.handle(this.attachToken(req));
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.forceLogout();
          return throwError(() => err);
        }),
      );
    }

    // A refresh triggered by a parallel request is already in flight — wait for it to land, then
    // retry THIS request with whatever token comes out the other end, instead of firing a second
    // concurrent refresh call.
    return this.refreshedToken$.pipe(
      filter((token): token is string => token !== null),
      take(1),
      switchMap(() => next.handle(this.attachToken(req))),
    );
  }

  private forceLogout(): void {
    // Mirrors the keys Login.ts writes / dashboard.component.ts's own logout() clears — a refresh
    // failure means the session is genuinely over, so this clears the same full set.
    [
      'accessToken', 'token', 'refreshToken', 'user', 'currentUser', 'currentClient',
      'currentProduct', 'CurrentProductUId', 'CurrentClientUId', 'CurrentRoleBasedlogedin',
      'CurrentloggedInUId', 'CurrentDeviceID', 'EmailId', 'CompanyName', 'CompanyLogo',
    ].forEach((k) => localStorage.removeItem(k));
    this.router.navigate(['/login']);
  }
}
